import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

const QUICK_ADD_COLOR_FILTER_TEMPLATE_SUFFIXES = [
  'multi-pants',
  'pants-waist-inseam-colour',
  'top-size-colour-fit',
  'ds-carhartt-active',
  'reworked-bag',
];

function normalizeQuickAddValue(value) {
  return (value || '').toString().trim().toLowerCase();
}

function matchesQuickAddColorOption(labelText, nameText = '') {
  return (
    labelText.includes('color') ||
    labelText.includes('colour') ||
    nameText.includes('options[color]') ||
    nameText.includes('options[colour]')
  );
}

function getQuickAddStateHost(container) {
  if (!(container instanceof Element)) return null;

  return container.closest('quick-add-dialog') || container;
}

function setQuickAddColorFilterActive(container, active) {
  const stateHost = getQuickAddStateHost(container);
  if (!(stateHost instanceof HTMLElement)) return;

  stateHost.dataset.quickAddColorFilterActive = active ? 'true' : 'false';
}

function isQuickAddColorFilterActive(container) {
  const stateHost = getQuickAddStateHost(container);
  return stateHost instanceof HTMLElement && stateHost.dataset.quickAddColorFilterActive === 'true';
}

function getQuickAddVariantPicker(container) {
  return container.querySelector('variant-picker');
}

function getQuickAddGallery(container) {
  return container.querySelector('media-gallery');
}

function getQuickAddSlideshow(container) {
  return container.querySelector('media-gallery slideshow-component');
}

function getQuickAddSlides(slideshow) {
  if (Array.isArray(slideshow?.refs?.slides) && slideshow.refs.slides.length) {
    return slideshow.refs.slides;
  }

  return Array.from(slideshow?.querySelectorAll('slideshow-slide') || []);
}

function getQuickAddThumbnails(slideshow) {
  if (Array.isArray(slideshow?.refs?.thumbnails) && slideshow.refs.thumbnails.length) {
    return slideshow.refs.thumbnails;
  }

  return [];
}

function getQuickAddAlt(element) {
  const image = element?.querySelector?.('img');
  return normalizeQuickAddValue(image?.getAttribute('alt'));
}

function showQuickAddGalleryItem(element) {
  if (!(element instanceof HTMLElement)) return;

  element.removeAttribute('hidden');
  element.setAttribute('aria-hidden', 'false');
  element.classList.remove('hz-color-hidden');
}

function hideQuickAddGalleryItem(element) {
  if (!(element instanceof HTMLElement)) return;

  element.setAttribute('hidden', '');
  element.setAttribute('aria-hidden', 'true');
  element.classList.add('hz-color-hidden');
}

function shouldUseQuickAddColorFilter(container) {
  const gallery = getQuickAddGallery(container);
  if (!(gallery instanceof HTMLElement)) return false;

  const templateSuffix = normalizeQuickAddValue(gallery.dataset.hzTemplateSuffix);
  if (!templateSuffix) return false;

  return QUICK_ADD_COLOR_FILTER_TEMPLATE_SUFFIXES.includes(templateSuffix);
}

function isQuickAddColorInput(target, variantPicker) {
  if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return false;
  if (!(variantPicker instanceof HTMLElement)) return false;

  if (target instanceof HTMLSelectElement) {
    const label = Array.from(variantPicker.querySelectorAll('label')).find((candidate) => candidate.htmlFor === target.id);
    const labelText = normalizeQuickAddValue(label?.textContent);
    const nameText = normalizeQuickAddValue(target.name);

    return matchesQuickAddColorOption(labelText, nameText);
  }

  const fieldset = target.closest('fieldset.variant-option');
  const legendText = normalizeQuickAddValue(fieldset?.querySelector('legend')?.textContent);
  const nameText = normalizeQuickAddValue(target.name);

  return matchesQuickAddColorOption(legendText, nameText);
}

function getQuickAddSelectedColor(container) {
  const picker = getQuickAddVariantPicker(container);
  if (!(picker instanceof HTMLElement)) return '';

  const selects = picker.querySelectorAll('select[name^="options["]');
  for (const select of selects) {
    const label = picker.querySelector(`label[for="${select.id}"]`);
    const labelText = normalizeQuickAddValue(label?.textContent);
    const nameText = normalizeQuickAddValue(select.name);

    if (matchesQuickAddColorOption(labelText, nameText)) {
      return select.value || '';
    }
  }

  const fieldsets = picker.querySelectorAll('fieldset.variant-option');
  for (const fieldset of fieldsets) {
    const legendText = normalizeQuickAddValue(fieldset.querySelector('legend')?.textContent);

    if (matchesQuickAddColorOption(legendText)) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement) {
        return checked.value || '';
      }
    }
  }

  const checkedInputs = picker.querySelectorAll('input[type="radio"]:checked');
  for (const input of checkedInputs) {
    if (!(input instanceof HTMLInputElement)) continue;

    const nameText = normalizeQuickAddValue(input.name);
    if (nameText.includes('color') || nameText.includes('colour')) {
      return input.value || '';
    }
  }

  return '';
}

function showAllQuickAddGalleryItems(container) {
  const slideshow = getQuickAddSlideshow(container);
  if (!slideshow) return;

  getQuickAddSlides(slideshow).forEach(showQuickAddGalleryItem);
  getQuickAddThumbnails(slideshow).forEach(showQuickAddGalleryItem);
}

function syncQuickAddColorGallery(container, forceReset = false) {
  const slideshow = getQuickAddSlideshow(container);
  if (!slideshow) return;

  if (!shouldUseQuickAddColorFilter(container) || !isQuickAddColorFilterActive(container)) {
    showAllQuickAddGalleryItems(container);
    return;
  }

  const selectedColor = normalizeQuickAddValue(getQuickAddSelectedColor(container));
  if (!selectedColor) {
    showAllQuickAddGalleryItems(container);
    return;
  }

  const slides = getQuickAddSlides(slideshow);
  const thumbnails = getQuickAddThumbnails(slideshow);

  if (!slides.length) return;

  const visibleIndexes = [];

  slides.forEach((slide, index) => {
    if (!(slide instanceof HTMLElement)) return;

    const isMeasurementSlide = slide.classList.contains('product-media-container--mvs');
    const alt = getQuickAddAlt(slide);

    if (isMeasurementSlide || alt === selectedColor) {
      showQuickAddGalleryItem(slide);
      visibleIndexes.push(index);
    } else {
      hideQuickAddGalleryItem(slide);
    }
  });

  thumbnails.forEach((thumbnail, index) => {
    const relatedSlide = slides[index];
    const isMeasurementSlide = relatedSlide instanceof HTMLElement && relatedSlide.classList.contains('product-media-container--mvs');
    const alt = relatedSlide ? getQuickAddAlt(relatedSlide) : getQuickAddAlt(thumbnail);

    if (isMeasurementSlide || alt === selectedColor) {
      showQuickAddGalleryItem(thumbnail);
    } else {
      hideQuickAddGalleryItem(thumbnail);
    }
  });

  if (!visibleIndexes.length) {
    showAllQuickAddGalleryItems(container);
    return;
  }

  let targetIndex = visibleIndexes[0];

  if (!forceReset && typeof slideshow.current === 'number' && Array.isArray(slideshow.slides)) {
    const currentVisibleSlide = slideshow.slides[slideshow.current];
    const currentOriginalIndex = slides.indexOf(currentVisibleSlide);

    if (visibleIndexes.includes(currentOriginalIndex)) {
      targetIndex = currentOriginalIndex;
    }
  }

  const targetSlide = slides[targetIndex];
  const targetSlideId = targetSlide?.getAttribute('slide-id');

  if (typeof slideshow.select === 'function') {
    requestAnimationFrame(() => {
      if (targetSlideId) {
        slideshow.select({ id: targetSlideId }, undefined, { animate: false });
      } else {
        slideshow.select(0, undefined, { animate: false });
      }
    });
  }
}

function syncQuickAddAvailableOptions(container) {
  const variantPicker = getQuickAddVariantPicker(container);
  if (!(variantPicker instanceof HTMLElement)) return false;

  for (const select of variantPicker.querySelectorAll('select.variant-option__select')) {
    if (!(select instanceof HTMLSelectElement)) continue;

    const options = Array.from(select.options);
    const firstAvailableOption = options.find((option) => option.dataset.optionAvailable !== 'false') || null;
    let selectedUnavailable = false;

    options.forEach((option) => {
      const isAvailable = option.dataset.optionAvailable !== 'false';
      option.hidden = !isAvailable;
      option.disabled = !isAvailable;

      if (option.selected && !isAvailable) {
        selectedUnavailable = true;
      }
    });

    if (selectedUnavailable && firstAvailableOption) {
      options.forEach((option) => option.removeAttribute('selected'));
      firstAvailableOption.selected = true;
      firstAvailableOption.setAttribute('selected', 'selected');
      select.value = firstAvailableOption.value;

      queueMicrotask(() => {
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });

      return true;
    }
  }

  for (const fieldset of variantPicker.querySelectorAll('fieldset.variant-option')) {
    if (!(fieldset instanceof HTMLElement)) continue;

    const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"][data-option-available]'));
    const firstAvailableInput = inputs.find((input) => input.dataset.optionAvailable !== 'false') || null;
    let checkedUnavailable = false;

    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const label = input.closest('label');
      const isAvailable = input.dataset.optionAvailable !== 'false';

      if (label instanceof HTMLElement) {
        label.hidden = !isAvailable;
      }

      input.disabled = !isAvailable;

      if (input.checked && !isAvailable) {
        checkedUnavailable = true;
      }
    });

    if (checkedUnavailable && firstAvailableInput instanceof HTMLInputElement) {
      firstAvailableInput.checked = true;

      queueMicrotask(() => {
        firstAvailableInput.dispatchEvent(new Event('change', { bubbles: true }));
      });

      return true;
    }
  }

  return false;
}

function applyQuickAddMobileMediaStack(container) {
  if (!isMobileBreakpoint() || !(container instanceof Element)) return;

  const mediaWrapper = container.querySelector('.product-information__media');
  if (!(mediaWrapper instanceof HTMLElement)) return;

  mediaWrapper.setAttribute('data-quick-add-mobile-media', 'true');

  mediaWrapper.querySelectorAll('slideshow-slide').forEach((slide) => {
    if (!(slide instanceof HTMLElement)) return;

    slide.removeAttribute('hidden');
    slide.setAttribute('reveal', '');
    slide.setAttribute('aria-hidden', 'false');
  });
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    setQuickAddColorFilterActive(modalContent, false);

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');

      // Only run the mobile-specific restructure when every expected piece is present.
      // Otherwise fall through and morph the full grid so the modal still has content.
      if (productDetails && productFormComponent && variantPicker && productPrice) {
        const productTitle = document.createElement('a');
        productTitle.textContent = this.dataset.productTitle || '';
        productTitle.href = this.productPageUrl;

        const productHeader = document.createElement('div');
        productHeader.classList.add('product-header');

        productHeader.appendChild(productTitle);
        productHeader.appendChild(productPrice);
        productGrid.appendChild(productHeader);
        productGrid.appendChild(variantPicker);
        productGrid.appendChild(productFormComponent);
        productDetails.remove();
      }
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    if (syncQuickAddAvailableOptions(modalContent)) return;
    applyQuickAddMobileMediaStack(modalContent);
    showAllQuickAddGalleryItems(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /** @type {AbortController | null} */
  #variantImageAbort = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink, { signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateModalMedia, { signal });

    // Safety net: also listen on document. Captures variant updates dispatched from
    // anywhere inside the modal even if intermediate handlers stop bubbling.
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleDocumentVariantUpdate, { signal });

    // Direct fallback: when any variant input inside the modal changes, fetch the
    // product page ourselves and update the media-gallery. Independent of Shopify's
    // VariantUpdateEvent flow so it always works.
    this.addEventListener('change', this.#handleVariantInputChange, { signal });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  /**
   * Replaces the modal's media-gallery with the variant-specific one from the fetched HTML
   * so the product image updates when the user picks a different color/variant.
   * @param {CustomEvent} event - The variant update event
   */
  #updateModalMedia = (event) => {
    const sourceHtml = /** @type {Document | undefined} */ (event.detail?.data?.html);
    if (!sourceHtml) return;

    this.#swapModalMedia(sourceHtml);
  };

  /**
   * Hard-replaces the modal's media wrapper / media-gallery with the matching one from a
   * fetched product-page document. Forces a fresh element so the slideshow and child custom
   * elements re-mount and visibly reflect the new variant image.
   * @param {Document} sourceHtml
   */
  #swapModalMedia = (sourceHtml) => {
    const newMediaWrapper = sourceHtml.querySelector('.product-information__media');
    const currentMediaWrapper = this.querySelector('.product-information__media');

    if (newMediaWrapper && currentMediaWrapper) {
      currentMediaWrapper.replaceWith(newMediaWrapper.cloneNode(true));
      applyQuickAddMobileMediaStack(this);
      if (isQuickAddColorFilterActive(this)) {
        syncQuickAddColorGallery(this, true);
      } else {
        showAllQuickAddGalleryItems(this);
      }
      return;
    }

    const newMediaGallery = sourceHtml.querySelector('media-gallery');
    const currentMediaGallery = this.querySelector('media-gallery');
    if (!newMediaGallery || !currentMediaGallery) return;

    currentMediaGallery.replaceWith(newMediaGallery.cloneNode(true));
    applyQuickAddMobileMediaStack(this);
    if (isQuickAddColorFilterActive(this)) {
      syncQuickAddColorGallery(this, true);
    } else {
      showAllQuickAddGalleryItems(this);
    }
  };

  /**
   * Document-level safety-net handler. Only acts on events that originated inside the
   * dialog so we don't double-handle product page updates.
   * @param {Event} event - The variant update event
   */
  #handleDocumentVariantUpdate = (event) => {
    const dialog = this.refs?.dialog;
    if (!dialog?.open) return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    this.#updateModalMedia(/** @type {CustomEvent} */ (event));
  };

  /**
   * Direct handler. When any variant-related input inside the modal changes:
   *  - resolves the FIRST AVAILABLE variant for that option using data-variant-id,
   *  - fetches the product page for that variant,
   *  - swaps the modal's variant-picker (so other dropdowns auto-correct to available values)
   *    and media-gallery (so the image reflects the new variant).
   * Works independently of Shopify's VariantUpdateEvent, so it stays robust even when other
   * listeners throw.
   * @param {Event} event
   */
  #handleVariantInputChange = (event) => {
    const dialog = this.refs?.dialog;
    if (!dialog?.open) return;

    const target = event.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;

    const variantPicker = target.closest('variant-picker');
    if (!variantPicker) return;

    if (isQuickAddColorInput(target, variantPicker)) {
      setQuickAddColorFilterActive(this, true);
      syncQuickAddColorGallery(this, true);
    } else if (isQuickAddColorFilterActive(this)) {
      syncQuickAddColorGallery(this);
    }

    const productUrl = variantPicker.getAttribute('data-product-url');
    if (!productUrl) return;
    const requestUrl = this.#buildQuickAddVariantRequestUrl(target, variantPicker, productUrl);
    if (!requestUrl) return;

    this.#variantImageAbort?.abort();
    this.#variantImageAbort = new AbortController();

    fetch(requestUrl, { signal: this.#variantImageAbort.signal })
      .then((response) => response.text())
      .then((html) => {
        const sourceHtml = new DOMParser().parseFromString(html, 'text/html');
        this.#swapModalVariantPicker(sourceHtml, variantPicker);
        this.#swapModalMedia(sourceHtml);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[quick-add] Failed to update modal image:', error);
        }
      });
  };

  /**
   * Builds a full product-page request URL for the modal using the currently selected
   * option values. This is required for multi-option products where choosing a color
   * alone is not enough to identify the correct media state.
   * @param {HTMLInputElement | HTMLSelectElement} target
   * @param {Element} variantPicker
   * @param {string} productUrl
   * @returns {string | null}
   */
  #buildQuickAddVariantRequestUrl = (target, variantPicker, productUrl) => {
    if (typeof variantPicker.buildRequestUrl === 'function') {
      const optionValuesUrl = variantPicker.buildRequestUrl(target);
      if (optionValuesUrl) {
        return optionValuesUrl.replace(/([?&])section_id=section-rendering-product-card(&|$)/, '$1').replace(/[?&]$/, '');
      }
    }

    // Fallback: use a specific variant id if the picker API is not available.
    const variantId = this.#resolveSelectedVariantId(target, variantPicker);
    if (!variantId) return null;

    return `${productUrl.split('?')[0]}?variant=${variantId}`;
  };

  /**
   * Reads a variant ID from the changed option. Falls back to the picker's own selected
   * variant id attribute when the changed input doesn't carry one (rare edge cases).
   * @param {HTMLInputElement | HTMLSelectElement} target
   * @param {Element} variantPicker
   * @returns {string | null}
   */
  #resolveSelectedVariantId = (target, variantPicker) => {
    if (target instanceof HTMLSelectElement) {
      const selected = target.options[target.selectedIndex];
      const id = selected?.dataset.variantId;
      if (id) return id;
    } else if (target.dataset.variantId) {
      return target.dataset.variantId;
    }

    const fallback = variantPicker.querySelector(
      'fieldset input[type="radio"]:checked[data-variant-id], select option:checked[data-variant-id]'
    );
    return /** @type {HTMLElement | null} */ (fallback)?.dataset.variantId ?? null;
  };

  /**
   * Replaces the modal's variant-picker with the one from the freshly-fetched product page,
   * so options that became unavailable for the new variant are reflected automatically.
   * @param {Document} sourceHtml
   * @param {Element} currentVariantPicker
   */
  #swapModalVariantPicker = (sourceHtml, currentVariantPicker) => {
    const newVariantPicker = sourceHtml.querySelector('variant-picker');
    if (!newVariantPicker) return;

    morph(currentVariantPicker, newVariantPicker);
    syncQuickAddAvailableOptions(this);
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
